import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { toJpeg } from "html-to-image";
import { QRCodeCanvas } from "qrcode.react";
import { Download, Loader2, Image as ImageIcon } from "lucide-react";
import ScToolkitBack from "../../components/sc/ScToolkitBack";

export default function ScCampaigns() {
  const [campaigns, setCampaigns] = useState(null);
  const [leadUrl, setLeadUrl] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);
  const refs = useRef({});

  async function load() {
    try {
      const [{ data: camps }, { data: flyer }] = await Promise.all([
        api.get("/sc/campaigns"),
        api.get("/sc/flyer-data"),
      ]);
      setCampaigns(camps || []);
      setLeadUrl(flyer.lead_url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setCampaigns([]);
    }
  }

  useEffect(() => { load(); }, []);

  async function downloadWithQr(c) {
    const node = refs.current[c.id];
    if (!node) return;
    setDownloadingId(c.id);
    try {
      const dataUrl = await toJpeg(node, { quality: 0.95, pixelRatio: 2.5, backgroundColor: "#ffffff" });
      const link = document.createElement("a");
      link.download = `purvodaya-solar-${c.title.replace(/\s+/g, "-").toLowerCase()}.jpg`;
      link.href = dataUrl;
      link.click();
    } catch {
      toast.error("Could not generate the image — please try again");
    } finally {
      setDownloadingId(null);
    }
  }

  if (!campaigns) return <div className="font-mono text-xs text-slate-400 mt-10 text-center" data-testid="sc-campaigns-loading">Loading…</div>;

  return (
    <div data-testid="sc-campaigns">
      <ScToolkitBack />
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">Toolkit</div>
      <h1 className="text-2xl font-bold tracking-tight mt-1 text-slate-900">Creatives.</h1>
      <p className="text-xs text-slate-500 mt-1.5">Company-approved WhatsApp creatives with the Purvodaya logo and your personal QR code baked in. Download and share directly.</p>

      {campaigns.length === 0 && (
        <div className="text-center py-14 bg-white border border-slate-200 rounded-2xl mt-4">
          <ImageIcon className="w-8 h-8 mx-auto text-slate-300" />
          <div className="font-mono text-xs text-slate-400 mt-3">No creatives published yet. Check back soon!</div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3">
        {campaigns.map((c) => (
          <div key={c.id} className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(15,23,42,0.04)]" data-testid={`sc-campaign-${c.id}`}>
            <div
              ref={(el) => { refs.current[c.id] = el; }}
              style={{ width: "100%", background: "#fff" }}
            >
              {/* Creative image + Purvodaya logo badge, top-left */}
              <div className="relative" style={{ width: "100%", aspectRatio: "1/1", background: "#f4f6f1" }}>
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/campaigns/image/${c.image_path}`}
                  alt={c.title}
                  crossOrigin="anonymous"
                  className="w-full h-full object-cover"
                />
                <div style={{
                  position: "absolute", top: 6, left: 6, background: "rgba(255,255,255,0.92)",
                  borderRadius: 8, padding: "3px 6px 3px 3px", display: "flex", alignItems: "center", gap: 4,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                }}>
                  <img src="/purvodaya-logo.png" alt="" crossOrigin="anonymous" style={{ height: 18, width: "auto" }} />
                  <span style={{ fontSize: 7.5, fontWeight: 700, color: "#0F2A14", letterSpacing: 0.2 }}>PURVODAYA SOLAR</span>
                </div>
              </div>

              {/* Footer band: QR + CTA copy */}
              <div style={{ background: "#1B5E20", padding: "8px 8px", display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ background: "white", padding: 3, borderRadius: 6, flexShrink: 0 }}>
                  <QRCodeCanvas value={leadUrl} size={40} />
                </div>
                <div style={{ fontSize: 7.2, fontWeight: 600, color: "#FFFFFF", lineHeight: 1.25 }}>
                  Scan the QR Code to connect with our Solar Experts
                </div>
              </div>
            </div>
            <div className="p-2.5 bg-white">
              <div className="text-[12px] font-semibold text-slate-900 truncate">{c.title}</div>
              <button
                onClick={() => downloadWithQr(c)}
                disabled={downloadingId === c.id}
                data-testid={`sc-campaign-download-${c.id}`}
                className="mt-2 w-full bg-[#1B5E20] hover:bg-[#15501A] text-white py-2 rounded-lg text-[10px] font-semibold uppercase tracking-wider flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                {downloadingId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                {downloadingId === c.id ? "…" : "Download"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
